import EVENT_CRUD_TYPES from '../components/common/event-crud-types';
import EVENT_TYPES from '../components/common/event-type';
import DEVICE_TYPE from '../components/enum/device_type';
import LOCATION_TYPE_ID from '../components/enum/location_type';

export const COLLECT_DELIVERY_LOCATION_TYPE_IDS = [
  LOCATION_TYPE_ID.Bedside,
  LOCATION_TYPE_ID.Curbside,
  LOCATION_TYPE_ID.Courier,
  LOCATION_TYPE_ID.Counter,
];

export const isCollectDeliveryLocationType = (locationTypeId) =>
  COLLECT_DELIVERY_LOCATION_TYPE_IDS.indexOf(locationTypeId) !== -1;

export const getCollectDeliveryLocationLabel = (locationTypeId) => {
  if (locationTypeId === LOCATION_TYPE_ID.Curbside) return 'Curbside';
  if (locationTypeId === LOCATION_TYPE_ID.Courier) return 'Courier';
  if (locationTypeId === LOCATION_TYPE_ID.Bedside) return 'Bedside';
  if (locationTypeId === LOCATION_TYPE_ID.Counter) return 'Counter';
  return 'Delivery';
};

export const COLLECT_DELIVERY_MESSAGES = {
  noUser: 'Select or scan a delivery user.',
  emptyCart: 'Add at least one order to the cart.',
  emptyStock: 'Scan or enter a stock code.',
  alreadyInCart: 'This order is already in the cart.',
  mixedLocation: 'All selected orders must belong to the same location.',
  loadUsers: 'Unable to load delivery users.',
  addStock: 'Unable to add this stock code.',
  confirmFailed: 'Unable to confirm delivery collection.',
  badgeNotFound: 'No enabled user found for this Badge ID.',
  notReady: 'Ready order not found for this stock code.',
  notifyFailed: 'Orders were collected, but patient SMS or email could not be sent.',
  partialFailed: 'Some orders could not be collected. Remaining orders are still in the cart.',
};

export const getAuthToken = () => {
  const stored = sessionStorage.getItem('user');
  if (!stored) return '';
  try {
    const user = JSON.parse(stored);
    return (
      (user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken) ||
      ''
    );
  } catch (error) {
    return '';
  }
};

export const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

export const normalizeBadgeId = (value) =>
  String(value || '')
    .replace(/[\r\n\t]/g, '')
    .trim();

export const normalizeStockCode = (value) =>
  String(value || '')
    .replace(/[\r\n\t]/g, '')
    .trim()
    .toUpperCase();

export const getDeliveryUserLabel = (user) => {
  if (!user) return '';
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.name || user.username || '';
};

export const filterEnabledUsers = (users) =>
  Array.isArray(users)
    ? users.filter(
        (deliveryUser) =>
          deliveryUser &&
          deliveryUser.is_deleted !== true &&
          deliveryUser.is_enabled !== false
      )
    : [];

export const findUserByBadgeId = (users, badgeId) => {
  const normalized = normalizeBadgeId(badgeId).toLowerCase();
  if (!normalized) return null;
  const matches = (users || []).filter(
    (deliveryUser) =>
      deliveryUser &&
      deliveryUser.badge_id &&
      String(deliveryUser.badge_id).trim().toLowerCase() === normalized
  );
  if (matches.length === 1) return matches[0];
  return null;
};

export const isOrderInCart = (cart, order) => {
  if (!order) return false;
  return (cart || []).some((item) => {
    if (order.id && item.id === order.id) return true;
    if (order.stock_code && item.stock_code) {
      return (
        String(item.stock_code).trim().toUpperCase() ===
        String(order.stock_code).trim().toUpperCase()
      );
    }
    return false;
  });
};

export const fetchDeliveryUsers = async () => {
  const response = await fetch(`${process.env.REACT_APP_BASEURL}users`, {
    method: 'GET',
    withCredentials: true,
    headers: authHeaders(),
  });
  const users = await response.json();
  return filterEnabledUsers(users);
};

export const lookupDeliveryUserByBadgeId = async (badgeId) => {
  const normalized = normalizeBadgeId(badgeId);
  if (!normalized) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.noUser };
  }

  const response = await fetch(
    `${process.env.REACT_APP_BASEURL}deliveryconsole/lookup-delivery-user`,
    {
      method: 'POST',
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({ badge_id: normalized }),
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success || !result.user) {
    return {
      success: false,
      message: (result && result.message) || COLLECT_DELIVERY_MESSAGES.badgeNotFound,
    };
  }
  return { success: true, user: result.user };
};

const addReadyOrderFromResponse = async (response) => {
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success || !result.order) {
    return {
      success: false,
      message: (result && result.message) || COLLECT_DELIVERY_MESSAGES.notReady,
    };
  }
  return { success: true, order: result.order };
};

export const collectOrderByStock = async (stockCode, locationTypeId) => {
  const normalized = normalizeStockCode(stockCode);
  if (!normalized) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.emptyStock };
  }
  if (!isCollectDeliveryLocationType(locationTypeId)) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.notReady };
  }
  const response = await fetch(
    `${process.env.REACT_APP_BASEURL}deliveryconsole/bedside/collect-order-by-stock`,
    {
      method: 'POST',
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        stock_code: normalized,
        location_type_id: locationTypeId,
      }),
    }
  );
  return addReadyOrderFromResponse(response);
};

export const collectOrderById = async (orderId, locationTypeId) => {
  if (!orderId) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.notReady };
  }
  if (!isCollectDeliveryLocationType(locationTypeId)) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.notReady };
  }
  const response = await fetch(
    `${process.env.REACT_APP_BASEURL}deliveryconsole/bedside/collect-order-by-id`,
    {
      method: 'POST',
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        order_id: orderId,
        location_type_id: locationTypeId,
      }),
    }
  );
  return addReadyOrderFromResponse(response);
};

export const confirmCollectDelivery = async ({ userId, cart, locationTypeId }) => {
  if (!userId && locationTypeId !== LOCATION_TYPE_ID.Bedside) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.noUser };
  }
  if (!cart || cart.length === 0) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.emptyCart };
  }
  if (!isCollectDeliveryLocationType(locationTypeId)) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.confirmFailed };
  }

  const boxId = cart[0].box_id;
  const mixedLocation = cart.find(
    (order) =>
      order.box_id !== boxId ||
      (order.locationtype_id && order.locationtype_id !== locationTypeId)
  );
  if (mixedLocation) {
    return { success: false, message: COLLECT_DELIVERY_MESSAGES.mixedLocation };
  }

  const locationLabel = getCollectDeliveryLocationLabel(locationTypeId);
  const response = await fetch(
    `${process.env.REACT_APP_BASEURL}deliveryconsole/batch-updatestatus`,
    {
      method: 'POST',
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        order_ids: cart.map((order) => order.id),
        box_id: boxId,
        event_type_name: EVENT_TYPES.Out_for_Delivery,
        type_id: DEVICE_TYPE.Out_for_Delivery,
        location_type_id: locationTypeId,
        location_notes: '',
        event_name: EVENT_CRUD_TYPES.INSERT,
        description: `${locationLabel} collection for delivery (${cart.length} order(s))`,
        object_id: DEVICE_TYPE.Out_for_Delivery,
        delivery_user_id: userId,
        is_collect_delivery: true,
      }),
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success) {
    return {
      success: false,
      message:
        (result && (result.error || result.message)) ||
        COLLECT_DELIVERY_MESSAGES.confirmFailed,
      invalidOrderIds: result.invalid_order_ids || [],
    };
  }
  return {
    success: true,
    result,
    failedOrderIds: result.failed_order_ids || [],
    emailsSent: result.emails_sent,
    emailDetails: result.email_details || [],
  };
};



// WEBPACK FOOTER //
// ./src/utils/collectDelivery.js